/**
 * create3DSIframe Utility
 * Creates and manages 3DS authentication iframe
 */

import type { OnLogEvent } from '../types/logger';

export interface ThreeDSIframeConfig {
  acsUrl?: string;
  url?: string;
}

export interface ThreeDSIframeCallbacks {
  onComplete: (data: any) => void;
  onError: (error: Error) => void;
}

/**
 * Create 3DS authentication iframe
 * Returns cleanup function
 */
export const create3DSIframe = (
  config: ThreeDSIframeConfig,
  callbacks: ThreeDSIframeCallbacks,
  log?: OnLogEvent
): (() => void) => {
  // Create container for 3DS iframe if not exists
  let container = document.getElementById('three-ds-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'three-ds-container';
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.zIndex = '9999';
    container.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    document.body.appendChild(container);
  }

  // Create iframe for 3DS
  const iframe = document.createElement('iframe');
  iframe.src = config.acsUrl || config.url || '';
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  iframe.style.border = 'none';
  iframe.setAttribute('sandbox', 'allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation');

  // Declare messageListener first so it can be referenced in cleanup
  let messageListener: ((event: MessageEvent) => void) | null = null;

  const cleanup = () => {
    if (messageListener) {
      window.removeEventListener('message', messageListener);
    }
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  messageListener = (event: MessageEvent) => {
    if (event.data && typeof event.data === 'object') {
      if (event.data.type === 'payment_complete') {
        cleanup();
        callbacks.onComplete(event.data);
      } else if (event.data.type === 'payment_error') {
        cleanup();
        callbacks.onError(new Error(event.data.message || ''));
      }
    }
  };

  window.addEventListener('message', messageListener);

  iframe.onload = () => {
    log?.('info', 'Payment Processing', '3DS iframe loaded');
  };

  iframe.onerror = () => {
    cleanup();
    callbacks.onError(new Error());
  };

  container.appendChild(iframe);

  return cleanup;
};
